import type { ZoomPan } from './useZoomPan'
import { theme } from 'antd'
import { ImageCanvas } from './ImageCanvas'
import { PdfCanvas } from './PdfCanvas'
import { FilePlaceholder } from './FilePlaceholder'
import type { FileStatus } from './useDisplaySources'

export type DocumentViewerProps = {
  src: string | null
  status: FileStatus
  isPdf: boolean
  pageNumber: number
  rotate: number
  flipX?: boolean
  zoomPan: ZoomPan
  height: number
  background?: string
  onClick?: () => void
  onPdfLoad?: (numPages: number) => void
}

export const DocumentViewer = ({
  src,
  status,
  isPdf,
  pageNumber,
  rotate,
  flipX = false,
  zoomPan,
  height,
  background,
  onClick,
  onPdfLoad,
}: DocumentViewerProps) => {
  const { token } = theme.useToken()
  const viewerBackground = background ?? token.colorFillTertiary
  const {
    scale,
    position,
    isDragging,
    animate,
    wheelRef,
    dragHandlers,
    wasClick,
  } = zoomPan

  // Rendered outside the zoom/pan wrapper so the spinner is not rotated and
  // scaled along with the document.
  if (status !== 'ready' || !src) {
    return (
      <div
        style={{
          height,
          background: viewerBackground,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <FilePlaceholder status={status} />
      </div>
    )
  }

  const transform = `rotate(${rotate}deg) scale(${scale}) translate(${
    position.x / scale
  }px, ${position.y / scale}px)`
  const transition = isDragging
    ? 'none'
    : animate
    ? 'transform 0.3s ease'
    : 'transform 0.15s ease-out'

  return (
    <div
      ref={wheelRef}
      onMouseMove={dragHandlers.onMouseMove}
      onMouseUp={dragHandlers.onMouseUp}
      onMouseLeave={dragHandlers.onMouseUp}
      style={{
        position: 'relative',
        height,
        background: viewerBackground,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'zoom-in',
      }}
    >
      <div
        onMouseDown={dragHandlers.onMouseDown}
        onClick={
          isPdf
            ? () => {
                if (wasClick()) onClick?.()
              }
            : undefined
        }
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transform,
          transition,
          userSelect: 'none',
        }}
      >
        <div style={{ transform: flipX ? 'scaleX(-1)' : undefined }}>
          {isPdf ? (
            <PdfCanvas
              src={src}
              pageNumber={pageNumber}
              height={height}
              zoomScale={scale}
              onLoad={onPdfLoad}
            />
          ) : (
            <ImageCanvas src={src} />
          )}
        </div>
      </div>
    </div>
  )
}
